// AddressAutocomplete — NZ-restricted Google Places autocomplete built on the
// new Places API (New) via AutocompleteSuggestion.fetchAutocompleteSuggestions.
//
// Why this approach:
//   - The legacy google.maps.places.Autocomplete widget is NOT available to
//     new Google Cloud customers (post March 2025). We use the data-only
//     suggestion API and render our own dropdown to keep:
//       1. visual consistency with the shadcn Input + AeroMax design,
//       2. manual typing always working as a fallback,
//       3. NZ-only suggestions via `includedRegionCodes`.
//
// Structured fields returned to onPlaceSelected:
//   formatted_address, street_address, street_number, route, sublocality,
//   locality, region, postal_code, country, country_code, lat, lng
// `street_address` (street_number + route) is the address line WITHOUT the
// suburb/city/postcode — used by the CRM payload where those are sent separately.
import { useEffect, useRef, useState } from "react";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Input } from "@/components/ui/input";

function parseAddressComponentsNew(comps = []) {
  // Places API (New) returns components with { types, longText, shortText }
  const get = (type) =>
    comps.find((c) => c.types?.includes(type))?.longText || "";
  const getShort = (type) =>
    comps.find((c) => c.types?.includes(type))?.shortText || "";
  const street_number = get("street_number");
  const route = get("route");
  return {
    street_number,
    route,
    // Street line only (no suburb/city/postcode) — for the CRM address field.
    street_address: [street_number, route].filter(Boolean).join(" "),
    // True suburb, kept distinct from the town/city (locality) below.
    sublocality:
      get("sublocality") || get("sublocality_level_1") || get("neighborhood") || "",
    locality:
      get("locality") || get("sublocality") || get("postal_town") || "",
    region: get("administrative_area_level_1"),
    postal_code: get("postal_code"),
    country: get("country"),
    country_code: getShort("country"),
  };
}

function AutocompleteInputNew({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  ...inputProps
}) {
  const places = useMapsLibrary("places");
  const sessionTokenRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debRef = useRef(null);
  const containerRef = useRef(null);
  // While a place is being fetched, ignore stale input-driven fetches
  const justPickedRef = useRef(false);

  // (Re)create a session token whenever the places library becomes available.
  useEffect(() => {
    if (!places) return;
    sessionTokenRef.current = new places.AutocompleteSessionToken();
  }, [places]);

  // Debounced suggestion fetch on value change.
  useEffect(() => {
    if (justPickedRef.current) {
      justPickedRef.current = false;
      return;
    }
    if (!places || !value || value.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        const request = {
          input: value,
          sessionToken: sessionTokenRef.current,
          includedRegionCodes: ["nz"],
        };
        const { suggestions: results } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            request
          );
        // Only place-type suggestions (skip query-only ones).
        const placeOnly = (results || []).filter((s) => s.placePrediction);
        setSuggestions(placeOnly);
        setOpen(placeOnly.length > 0);
        setActiveIdx(-1);
      } catch (err) {
        // Silently fail — manual typing still works.
        setSuggestions([]);
        setOpen(false);
      }
    }, 220);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [places, value]);

  // Close dropdown on outside tap. `pointerdown` covers mouse + touch + pen,
  // so this also fires on iPad where `mousedown` is only synthesized.
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, []);

  const handlePick = async (sugg) => {
    if (!sugg?.placePrediction) return;
    justPickedRef.current = true;
    try {
      const place = sugg.placePrediction.toPlace();
      await place.fetchFields({
        fields: ["formattedAddress", "addressComponents", "location"],
      });
      const parts = parseAddressComponentsNew(place.addressComponents || []);
      const formatted =
        place.formattedAddress ||
        sugg.placePrediction.text?.toString() ||
        value;
      onChange?.(formatted);
      onPlaceSelected?.({
        formatted_address: formatted,
        ...parts,
        lat: place.location?.lat?.() ?? null,
        lng: place.location?.lng?.() ?? null,
      });
      // Refresh the session token after each billable selection.
      if (places) {
        sessionTokenRef.current = new places.AutocompleteSessionToken();
      }
    } catch (err) {
      // Couldn't hydrate the place — fall back to the prediction text.
      onChange?.(sugg.placePrediction.text?.toString() || value);
    } finally {
      setOpen(false);
      setSuggestions([]);
    }
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      handlePick(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <Input
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        inputMode="text"
        {...inputProps}
      />
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          data-testid="address-autocomplete-list"
          className="absolute left-0 right-0 z-50 mt-1 w-full bg-white border border-[#E2E8F0] rounded-md shadow-lg max-h-72 overflow-auto"
        >
          {suggestions.map((sugg, i) => {
            const text = sugg.placePrediction?.text?.toString() || "";
            const main =
              sugg.placePrediction?.mainText?.toString() || text;
            const secondary =
              sugg.placePrediction?.secondaryText?.toString() || "";
            return (
              <li
                key={i}
                role="option"
                aria-selected={i === activeIdx}
                data-testid={`address-suggestion-${i}`}
                className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                  i === activeIdx
                    ? "bg-[#10B981]/10 text-[#0B132B]"
                    : "hover:bg-[#F1F5F9]"
                }`}
                onPointerDown={(e) => {
                  // preventDefault keeps focus on the input (no blur/close race)
                  // and works for mouse + touch + pen, unlike onMouseDown which
                  // iOS Safari only synthesizes unreliably from a tap.
                  e.preventDefault();
                  handlePick(sugg);
                }}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <div className="font-medium text-[#0B132B] truncate">
                  {main}
                </div>
                {secondary && (
                  <div className="text-[12px] text-[#475569] truncate">
                    {secondary}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function AddressAutocomplete(props) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // No key configured → render a plain Input so manual typing still works.
  if (!apiKey) {
    return (
      <Input
        value={props.value}
        onChange={(e) => props.onChange?.(e.target.value)}
        placeholder={props.placeholder}
      />
    );
  }

  return (
    <APIProvider
      apiKey={apiKey}
      libraries={["places"]}
      solutionChannel="GMP_react-google-maps"
    >
      <AutocompleteInputNew {...props} />
    </APIProvider>
  );
}
